import { useEffect, useRef, useState, type ReactNode } from "react";
import { MOUNT_DEFERRED_EVENT } from "@/lib/scrollToSection";


interface DeferredRenderProps {
  children: ReactNode;
  /** Reserve approximately the rendered section's height to avoid scroll jumps. */
  minHeight: string;
  /** Begin rendering shortly before the section reaches the viewport. */
  rootMargin?: string;
  className?: string;
  /** Optional id placed on the outer wrapper so in-page anchors resolve
   *  even before the deferred children mount. */
  id?: string;
}

/**
 * Keeps below-the-fold sections out of the initial render and JS evaluation,
 * then mounts them shortly before the visitor reaches them.
 */
export function DeferredRender({
  children,
  minHeight,
  rootMargin = "800px 0px",
  className,
  id,
}: DeferredRenderProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (typeof IntersectionObserver === "undefined") {
      setReady(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setReady(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(element);

    // An in-page anchor was activated: mount now so the scroll can target the
    // real content instead of the reserved spacer (whose height is only an
    // approximation and shifts the section once the children mount).
    const onMountRequest = () => {
      setReady(true);
      observer.disconnect();
    };
    window.addEventListener(MOUNT_DEFERRED_EVENT, onMountRequest);

    // If the URL hash points inside this section, mount immediately so the
    // in-page anchor scroll lands on real content instead of an empty spacer.
    if (id && typeof window !== "undefined" && window.location.hash === `#${id}`) {
      setReady(true);
      observer.disconnect();
    }

    return () => {
      window.removeEventListener(MOUNT_DEFERRED_EVENT, onMountRequest);
      observer.disconnect();
    };
  }, [rootMargin, id]);


  return (
    <div
      id={id}
      ref={ref}
      className={className}
      style={ready ? undefined : { minHeight }}
      data-deferred-state={ready ? "ready" : "waiting"}
    >
      {ready ? children : null}
    </div>
  );
}

export default DeferredRender;
